import { TestDescription } from './decorators';

export interface HttpStatusParameter {
  statusText: string;
  status: number;
}

export function isHttpStatusResult(data: any): data is HttpStatusParameter {
  return data &&
    typeof data.statusText === 'string' &&
    typeof data.status === 'number';
}

export class HttpStatusResult {
  statusText: string|null = null;
  status: number = 0;

  is(compare: HttpStatusParameter) {
    return this.statusText === compare.statusText &&
      this.status === compare.status;
  }

  get hasConnectivity() {
    return !this.is(new ServerUnavailableResult()) &&
      !this.is(new OfflineResult()) &&
      !this.is(new GatewayTimeoutResult());
  }

  get succeeded() {
    return this.status >= 200 && this.status < 300;
  }

  // 2xx server errors
  get isSuccess() { return this.status == new SuccessResult().status; }
  get isOk() { return this.status == new OkResult().status; }
  get isCreated() { return this.status == new CreatedResult().status; }
  get isAccepted() { return this.status == new AcceptedResult().status; }
  get isNoContent() { return this.status == new NoContentResult().status; }

  // 4xx server errors
  get isBadRequest() { return this.status == new BadRequestResult().status; }
  get isInvalidModel() { return this.status == new InvalidModelResult().status; }
  get isInvalidParameter() { return this.status == new InvalidParameterResult().status; }
  get isMissingParameter() { return this.status == new MissingParameterResult().status; }
  get isInvalidClient() { return this.status == new InvalidClientResult().status; }
  get isVerificationFailed() { return this.status == new VerificationFailedResult().status; }
  get isEntityAlreadyExists() { return this.status == new EntityAlreadyExistsResult().status; }
  get isEntityNotExists() { return this.status == new EntityNotExistsResult().status; }
  get isInvalidToken() { return this.status == new InvalidTokenResult().status; }
  get isTooManyRequests() { return this.status == new TooManyRequestsResult().status; }
  get isFileNotExists() { return this.status == new FileNotExistsResult().status; }
  get isForbidden() { return this.status == new ForbiddenResult().status; }
  get isUnauthorized() { return this.status == new UnauthorizedResult().status; }
  get isInvalidJsonFormat() { return this.status == new InvalidJsonFormatResult().status; }
  get isInvalidXmlFormat() { return this.status == new InvalidXmlFormatResult().status; }
  get isRequirementsMismatch() { return this.status == new RequirementsMismatchResult().status; }
  get isUnsupportedTokenClaim() { return this.status == new UnsupportedTokenClaimResult().status; }
  get isNotFound() { return this.status == new NotFoundResult().status; }
  get isDirectoryNotExists() { return this.status == new DirectoryNotExistsResult().status; }
  get isConflict() { return this.status == new ConflictResult().status; }
  get isPayloadTooLarge() { return this.status == new PayloadTooLargeResult().status; }
  get isFileTooLong() { return this.status == new FileTooLongResult().status; }
  get isUnsupportedMediaType() { return this.status == new UnsupportedMediaTypeResult().status; }
  get isNotSupportedFileType() { return this.status == new NotSupportedFileTypeResult().status; }
  get isUnprocessableEntity() { return this.status == new UnprocessableEntityResult().status; }

  // 5xx server errors
  get isDbInternalError() { return this.status == new DbInternalErrorResult().status; }
  get isNotImplemented() { return this.status == new NotImplementedResult().status; }
  get isInternalError() { return this.status == new InternalErrorResult().status; }
  get isInternalServerError() { return this.status == new InternalServerErrorResult().status; }
  get isOperationFailed() { return this.status == new OperationFailedResult().status; }
  get isProtectorServiceError() { return this.status == new ProtectorServiceErrorResult().status; }
  get isGatewayTimeout() { return this.status == new GatewayTimeoutResult().status; }
  get isServiceUnavailable() { return this.status == new ServiceUnavailableResult().status; }

  // client errors
  get isServerUnavailable() { return this.status == new ServerUnavailableResult().status; }
  get isOffline() { return this.status == new OfflineResult().status; }
}

//#region 2xx server errors

@TestDescription('הצלחה')
export class OkResult extends HttpStatusResult {
  statusText = 'ok';
  status = 200;
}

@TestDescription('הצלחה')
export class SuccessResult extends HttpStatusResult {
  statusText = 'success';
  status = 200;
}

@TestDescription('המשאב המבוקש נוצר בהצלחה בשרת')
export class CreatedResult extends HttpStatusResult {
  statusText = 'created';
  status = 201;
}

@TestDescription('הבקשה התקבלה בשרת')
export class AcceptedResult extends HttpStatusResult {
  statusText = 'accepted';
  status = 202;
}

@TestDescription('הבקשה התקבלה בשרת אך לא נשלח תוכן')
export class NoContentResult extends HttpStatusResult {
  statusText = 'no content';
  status = 204;
}

//#endregion

//#region 4xx server errors

@TestDescription('הבקשה לא תקינה')
export class BadRequestResult extends HttpStatusResult {
  statusText = 'bad request';
  status = 400;
}

@TestDescription('הלקוח הזין נתונים לא תקפים')
export class InvalidModelResult extends BadRequestResult {
  statusText = 'invalid model';
  status = 400;
}

@TestDescription('פרמטר לא תקין')
export class InvalidParameterResult extends BadRequestResult {
  statusText = 'invalid parameter';
  status = 400;
}

@TestDescription('פרמטר חסר')
export class MissingParameterResult extends BadRequestResult {
  statusText = 'missing parameter';
  status = 400;
}

@TestDescription('אין הרשאה לגשת למשאב המבוקש בשרת')
export class UnauthorizedResult extends HttpStatusResult {
  statusText = 'unauthorized';
  status = 401;
}

@TestDescription('אין הרשאה לגשת למשאב המבוקש')
export class InvalidClientResult extends HttpStatusResult {
  statusText = 'invalid client';
  status = 401;
}

@TestDescription('הסיסמה לא תקינה או שהמשתמש לא קיים')
export class VerificationFailedResult extends HttpStatusResult {
  statusText = 'verification failed';
  status = 401;
}

@TestDescription('טוקן התחברות לא תקין')
export class InvalidTokenResult extends HttpStatusResult {
  statusText = 'invalid token';
  status = 401;
}

@TestDescription('לא ניתן לבצע את הפעולה עקב חוסר בדרישות')
export class RequirementsMismatchResult extends HttpStatusResult {
  statusText = 'requirements mismatch';
  status = 401;
}

@TestDescription('אחת מהדרישות בטוקן לא נתמכת')
export class UnsupportedTokenClaimResult extends HttpStatusResult {
  statusText = 'unsupported token claim';
  status = 401;
}

@TestDescription('למשתמש אין מספיק הרשאות כדי לבצע את הפעולה')
export class ForbiddenResult extends HttpStatusResult {
  statusText = 'forbidden';
  status = 403;
}

@TestDescription('המשאב המבוקש לא קיים בשרת')
export class NotFoundResult extends HttpStatusResult {
  statusText = 'not found';
  status = 404;
}

@TestDescription('השאב המבוקש לא קיים במערכת')
export class EntityNotExistsResult extends HttpStatusResult {
  statusText = 'entity not exists';
  status = 404;
}

@TestDescription('התיקייה המבוקשת לא קיימת בשרת')
export class DirectoryNotExistsResult extends HttpStatusResult {
  statusText = 'directory not exists';
  status = 404;
}

@TestDescription('הקובץ המבוקש לא קיים בשרת')
export class FileNotExistsResult extends HttpStatusResult {
  statusText = 'file not exists';
  status = 404;
}

@TestDescription('קיים כבר משאב כזה בשרת')
export class ConflictResult extends HttpStatusResult {
  statusText = 'conflict';
  status = 409;
}

@TestDescription('קיים כבר נתון כזה בשרת')
export class EntityAlreadyExistsResult extends HttpStatusResult {
  statusText = 'entity already exists';
  status = 409;
}

@TestDescription('המשאב גדול מדי עבור השרת')
export class PayloadTooLargeResult extends HttpStatusResult {
  statusText = 'payload too large';
  status = 413;
}

@TestDescription('הקובץ גדול מדי')
export class FileTooLongResult extends HttpStatusResult {
  statusText = 'file too long';
  status = 413;
}

@TestDescription('סוג המדיה שהתקבל לא נתמך בשרת')
export class UnsupportedMediaTypeResult extends HttpStatusResult {
  statusText = 'unsupported media type';
  status = 415;
}

@TestDescription('סוג הקובץ אינו נתמך ע"י המערכת')
export class NotSupportedFileTypeResult extends HttpStatusResult {
  statusText = 'not supported file type';
  status = 415;
}

@TestDescription('השרת נתקל במשאב שלא ניתן לפענוח')
export class UnprocessableEntityResult extends HttpStatusResult {
  statusText = 'unprocessable entity';
  status = 422;
}

@TestDescription('פורמט JSON לא תקין')
export class InvalidJsonFormatResult extends HttpStatusResult {
  statusText = 'invalid json format';
  status = 422;
}

@TestDescription('פורמט XML לא תקין')
export class InvalidXmlFormatResult extends HttpStatusResult {
  statusText = 'invalid xml format';
  status = 422;
}

@TestDescription('חסימה בעקבות בקשות רבות מדי לשרת')
export class TooManyRequestsResult extends HttpStatusResult {
  statusText = 'too many requests';
  status = 429;
}

//#endregion

//#region 5xx server errors

@TestDescription('שגיאה פנימית בשרת')
export class InternalServerErrorResult extends HttpStatusResult {
  statusText = 'internal server error';
  status = 500;
}

@TestDescription('שגיאה פנימית בבסיס הנתונים')
export class DbInternalErrorResult extends HttpStatusResult {
  statusText = 'db internal error';
  status = 500;
}

@TestDescription('שגיאה כללית')
export class InternalErrorResult extends HttpStatusResult {
  statusText = 'internal error';
  status = 500;
}

@TestDescription('הפעולה נכשלה')
export class OperationFailedResult extends HttpStatusResult {
  statusText = 'operation failed';
  status = 500;
}

@TestDescription('שגיאה בשירות ההצפנה בשרת')
export class ProtectorServiceErrorResult extends HttpStatusResult {
  statusText = 'protector service error';
  status = 500;
}

@TestDescription('הפעולה המובקשת לא ממומשת בשרת')
export class NotImplementedResult extends HttpStatusResult {
  statusText = 'not implemented';
  status = 501;
}

@TestDescription('השרת אינו זמין לבצע את הבקשה')
export class ServiceUnavailableResult extends HttpStatusResult {
  statusText = 'service unavailable';
  status = 503;
}

@TestDescription('פג תוקף הבקשה בשער הכניסה')
export class GatewayTimeoutResult extends HttpStatusResult {
  statusText = 'gateway timeout';
  status = 504;
}

//#endregion

//#region client errors

@TestDescription('השרת לא זמין')
export class ServerUnavailableResult extends HttpStatusResult {
  statusText = 'server unavailable model';
}

@TestDescription('הלקוח ללא חיבור לאינטרנט')
export class OfflineResult extends HttpStatusResult {
  statusText = 'offline';
}

//#endregion

export type HttpResultPromise<T = any> = Promise<HttpStatusResult | T>;

export function resultOf<T = HttpStatusResult>() {
  return Promise.resolve<HttpStatusResult | T>(new NotImplementedResult());
}
